import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { legacyDestination } from '../../lib/legacyPaths';

/**
 * "EFTCreator has moved" — the landing spot for a bookmark saved from the old site.
 *
 * v1 lived at this same domain, so `/dashboard` and its siblings now hit the v2 app, which matched no route
 * and rendered an empty document. Customers saw a black screen with nothing to click and no clue that
 * anything had changed, and reported it as the site being broken.
 *
 * It redirects rather than only offering a link because the people who arrive here did not choose to: they
 * clicked a bookmark expecting to be signed in and working. Ten seconds is long enough to read why, and the
 * countdown is announced so the redirect is never a surprise.
 */
const REDIRECT_SECONDS = 10;

export function Moved() {
  const { pathname } = useLocation();
  const dest = legacyDestination(pathname);
  const [left, setLeft] = useState(REDIRECT_SECONDS);
  /*
   * Cancelled by any deliberate interaction. Someone reading the page, or tabbing to the link they wanted,
   * should not have it pulled out from under them — a timer that fires mid-read is its own bug report.
   */
  const [cancelled, setCancelled] = useState(false);

  useEffect(() => {
    if (cancelled) return;
    if (left <= 0) {
      window.location.replace('/');
      return;
    }
    const t = setTimeout(() => setLeft((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [left, cancelled]);

  return (
    <div className="auth-page grain moved-page" onPointerDown={() => setCancelled(true)}>
      <div className="auth-page-bg" />
      <div className="panel moved-card">
        <h1 style={{ marginTop: 0 }}>EFTCreator has moved</h1>
        <p className="sub">
          You've reached a page from the previous version of EFTCreator. Everything now lives at{' '}
          <strong>eftcreator.com</strong> — your account, your transactions and your subscription all came
          with it, and you sign in with the same email and password.
        </p>

        <p className="moved-bookmark">
          <strong>Please update your bookmark</strong> to{' '}
          <span className="mono">https://eftcreator.com</span> so this doesn't happen again.
        </p>

        <div className="moved-actions">
          <a className="btn btn-primary" href="/">Go to EFTCreator</a>
          {/* Where we know what this page became, offer it — it is where they were actually heading. */}
          {dest.path && (
            <Link className="btn btn-ghost" to={dest.path} onClick={() => setCancelled(true)}>
              Take me to {dest.label}
            </Link>
          )}
        </div>

        <p className="moved-count" aria-live="polite">
          {cancelled
            ? 'Use a button above when you’re ready.'
            : `Taking you there automatically in ${left} second${left === 1 ? '' : 's'}…`}
        </p>
        {!cancelled && (
          <button type="button" className="linklike moved-stay" onClick={() => setCancelled(true)}>
            Stay on this page
          </button>
        )}
      </div>
    </div>
  );
}
